import { LAST_GOOD_PREFIX } from '../api/hooks';

/** Sign-in routing: where /login sends you afterwards, and how a lost session gets there. */

export const LOGIN_PATH = '/login';
const BASE = 'https://studio.invalid';

/**
 * The `next` target after sign-in, restricted to a path on this origin: `//evil.com`, `/\evil.com`,
 * `https://evil.com`, `javascript:` and control characters all fall back to `/`, as does /login itself.
 */
export function sanitizeNext(raw: string | null | undefined): string {
  if (!raw || raw.length > 2048) return '/';
  // biome-ignore lint/suspicious/noControlCharactersInRegex: rejecting control characters is the point
  if (!raw.startsWith('/') || raw.startsWith('//') || raw.startsWith('/\\') || /[\u0000-\u001f\\]/.test(raw))
    return '/';
  let url: URL;
  try {
    url = new URL(raw, BASE);
  } catch {
    return '/';
  }
  if (url.origin !== BASE) return '/';
  if (url.pathname === LOGIN_PATH || url.pathname.startsWith(`${LOGIN_PATH}/`)) return '/';
  return `${url.pathname}${url.search}${url.hash}`;
}

/** `/login?next=<where you were>` (plain `/login` when that is the home screen). */
export function loginPath(current: string): string {
  const next = sanitizeNext(current);
  return next === '/' ? LOGIN_PATH : `${LOGIN_PATH}?next=${encodeURIComponent(next)}`;
}

/** Sign-out leaves nothing readable behind: the last-good API responses kept for offline banners go too. */
export function clearLocalData(
  storage: Pick<Storage, 'length' | 'key' | 'removeItem'> | null = safeLocal(),
): void {
  if (storage === null) return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const k = storage.key(i);
      if (k?.startsWith(LAST_GOOD_PREFIX)) keys.push(k);
    }
    for (const k of keys) storage.removeItem(k);
  } catch {
    // Blocked storage holds nothing of ours.
  }
}

function safeLocal(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** 0–4 for the strength meter: length carries most of it (a long passphrase is strong), variety the rest. */
export function passwordStrength(pw: string): number {
  if (pw.length === 0) return 0;
  const classes = [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].filter((re) => re.test(pw)).length;
  if (pw.length < 12) return 1;
  let score = 2;
  if (pw.length >= 16 || classes >= 3) score++;
  if (pw.length >= 20 || (pw.length >= 16 && classes >= 3)) score++;
  return Math.min(4, score);
}

export const STRENGTH_LABEL = ['', 'Too short', 'Fair', 'Good', 'Strong'] as const;
